// The color theme is chosen server-side so the first paint is already in it: the /app layout
// loader resolves it and the root layout renders it as data-theme on <html>. The cookie is
// client-readable and client-written on purpose — it's presentation state, not a credential —
// so the server never trusts it: a theme that isn't enabled is ignored.

import { readCookieValue } from '~/lib/cookie-header';

export const THEME_COOKIE_NAME = 'se_theme';

/**
 * Id of the route whose loader resolves the theme: the /app layout. Remix derives a route's id
 * from its file path under app/, so moving that file means changing this.
 */
export const THEME_ROUTE_ID = 'routes/app';

export const DEFAULT_THEME = 'light';

export const DARK_THEME = 'dark';

export const SYSTEM_THEME = 'system';

/**
 * Every theme the app can render. Light ships; each other theme waits on its feature flag. System
 * isn't a palette of its own: CSS renders it light or dark to match the OS.
 */
export const THEMES = [DEFAULT_THEME, DARK_THEME, SYSTEM_THEME] as const;

export type ThemeName = (typeof THEMES)[number];

/** Reads the theme a user chose from a request's Cookie header, unvalidated. */
export function readThemeCookie(cookieHeader: string | null): string | undefined {
  return readCookieValue(cookieHeader, THEME_COOKIE_NAME);
}

/** The chosen theme when it's enabled; otherwise System when it's enabled, and light when not. */
export function resolveTheme(
  chosenTheme: string | undefined,
  enabledThemes: readonly ThemeName[],
): ThemeName {
  const enabledChoice = enabledThemes.find((currentTheme) => currentTheme === chosenTheme);
  if (enabledChoice !== undefined) {
    return enabledChoice;
  }
  return enabledThemes.includes(SYSTEM_THEME) ? SYSTEM_THEME : DEFAULT_THEME;
}
